import {
  Box,
  Select as ChakraSelect,
  Portal,
  Text,
  createListCollection,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';

export interface SelectOption<T extends string | number> {
  value: T;
  label: string;
}

export interface SelectProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  label?: string;
  /** When true, option values are numbers (coerced back on change). */
  numeric?: boolean;
  ariaLabel?: string;
  placeholder?: string;
}

/**
 * Figma-styled select — pdf-to-figma's, which is the one the family is matched to.
 *
 * This was a native `<select>`, themed at the edges but drawing the OS dropdown when opened:
 * a white list in a dark panel on macOS, and a different shape on every platform. pdf has
 * always rendered its own, and the difference shows the moment anyone clicks — which is why
 * the font-matching rows looked unlike pdf's despite using the same tokens.
 *
 * The typed API is kept as it was (`options`, `onChange(value)`, `numeric`), so the seven
 * apps already calling it did not have to change. Only what it draws moved.
 *
 * The list is PORTALLED with a large z-index. The plugin window is short and the settings
 * panel scrolls; a dropdown clipped by its own container would be unusable for exactly the
 * long lists this is for — a font list being the obvious one.
 */
export function Select<T extends string | number>({
  value,
  onChange,
  options,
  label,
  numeric = false,
  ariaLabel,
  placeholder,
}: SelectProps<T>) {
  const collection = useMemo(
    () =>
      createListCollection({
        items: options.map((o) => ({ label: o.label, value: String(o.value) })),
      }),
    [options],
  );

  return (
    <Box>
      {label && (
        <Text mb="1.5" fontSize="2xs" fontWeight="500" color="figma.textMuted">
          {label}
        </Text>
      )}
      <ChakraSelect.Root
        collection={collection}
        value={[String(value)]}
        onValueChange={(details: { value: string[] }) => {
          const next = details?.value?.[0];
          if (next === undefined) return;
          // `numeric` exists because the collection keys on strings: a caller whose values
          // are numbers must get a number back, not "3".
          onChange((numeric ? (Number(next) as T) : (next as T)));
        }}
      >
        <ChakraSelect.HiddenSelect aria-label={ariaLabel ?? label} />
        <ChakraSelect.Control>
          <ChakraSelect.Trigger
            h="8"
            px="3"
            bg="figma.panel"
            borderWidth="1px"
            borderColor="figma.border"
            borderRadius="md"
            fontSize="xs"
            color="figma.text"
            cursor="pointer"
            transition="all 0.15s ease"
            _hover={{ borderColor: 'figma.borderLight' }}
            _focus={{
              borderColor: 'figma.accent',
              boxShadow: '0 0 0 1px var(--chakra-colors-figma-accent)',
              outline: 'none',
            }}
            _expanded={{ borderColor: 'figma.accent' }}
          >
            <ChakraSelect.ValueText placeholder={placeholder} />
            <ChakraSelect.Indicator>
              <ChevronDown size={14} />
            </ChakraSelect.Indicator>
          </ChakraSelect.Trigger>
        </ChakraSelect.Control>
        <Portal>
          <ChakraSelect.Positioner style={{ zIndex: 9999 }}>
            <ChakraSelect.Content
              bg="figma.panel"
              borderWidth="1px"
              borderColor="figma.border"
              borderRadius="md"
              boxShadow="xl"
              maxH="250px"
              overflowY="auto"
            >
              {collection.items.map((item) => (
                <ChakraSelect.Item
                  key={item.value}
                  item={item}
                  px="3"
                  py="2"
                  fontSize="xs"
                  color="figma.text"
                  cursor="pointer"
                  transition="background 0.1s ease"
                  _hover={{ bg: 'figma.panelHover' }}
                  _highlighted={{ bg: 'figma.panelHover' }}
                  _checked={{ bg: 'figma.accentMuted', color: 'figma.accent' }}
                >
                  <ChakraSelect.ItemText>{item.label}</ChakraSelect.ItemText>
                  <ChakraSelect.ItemIndicator>
                    <Box w="1.5" h="1.5" borderRadius="full" bg="figma.accent" />
                  </ChakraSelect.ItemIndicator>
                </ChakraSelect.Item>
              ))}
            </ChakraSelect.Content>
          </ChakraSelect.Positioner>
        </Portal>
      </ChakraSelect.Root>
    </Box>
  );
}
